import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useReducedMotion } from 'framer-motion';
import { GeometricField } from './GeometricField';
function dotTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  if (ctx) {
    ctx.beginPath();
    ctx.arc(32, 32, 28, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  return tex;
}

/** Lightweight WebGL network: talent nodes, leadership connections and floating geometric forms. */
export function TalentNetwork({
  className = ''
}: {
  className?: string;
}) {
  const mountRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: 'low-power'
      });
    } catch {
      setFailed(true);
      return;
    }
    let width = mount.clientWidth || 600;
    let height = mount.clientHeight || 600;
    const small = width < 520;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.domElement.style.display = 'block';
    mount.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.z = 10;
    const group = new THREE.Group();
    scene.add(group);
    const disposables: {
      dispose: () => void;
    }[] = [];
    const track = <T extends {
      dispose: () => void;
    },>(o: T) => {
      disposables.push(o);
      return o;
    };

    // Nodes distributed on a softened sphere
    const count = small ? 70 : 120;
    const nodes: THREE.Vector3[] = [];
    for (let i = 0; i < count; i++) {
      const y = 1 - i / (count - 1) * 2;
      const r = Math.sqrt(1 - y * y);
      const theta = i * 2.399963;
      const radius = 3.1 + Math.sin(i * 12.9898) * 0.45;
      nodes.push(new THREE.Vector3(Math.cos(theta) * r * radius, y * radius, Math.sin(theta) * r * radius));
    }
    const tex = track(dotTexture());
    const leaders = nodes.filter((_, i) => i % 7 === 0);
    const talent = nodes.filter((_, i) => i % 7 !== 0);
    const goldPts = new THREE.Points(track(new THREE.BufferGeometry().setFromPoints(leaders)), track(new THREE.PointsMaterial({
      color: 0xc9a96e,
      size: 0.2,
      map: tex,
      transparent: true,
      alphaTest: 0.4
    })));
    const bluePts = new THREE.Points(track(new THREE.BufferGeometry().setFromPoints(talent)), track(new THREE.PointsMaterial({
      color: 0x93abd6,
      size: 0.1,
      map: tex,
      transparent: true,
      opacity: 0.85,
      alphaTest: 0.3
    })));
    group.add(goldPts, bluePts);
    const linePositions: number[] = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        if (nodes[i].distanceTo(nodes[j]) < 1.25) linePositions.push(nodes[i].x, nodes[i].y, nodes[i].z, nodes[j].x, nodes[j].y, nodes[j].z);
      }
    }
    const lineGeo = track(new THREE.BufferGeometry());
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
    group.add(new THREE.LineSegments(lineGeo, track(new THREE.LineBasicMaterial({
      color: 0x93abd6,
      transparent: true,
      opacity: 0.2
    }))));

    // Leadership core
    const coreMat = track(new THREE.LineBasicMaterial({
      color: 0xc9a96e,
      transparent: true,
      opacity: 0.9
    }));
    const core = new THREE.LineSegments(track(new THREE.EdgesGeometry(track(new THREE.OctahedronGeometry(0.7)))), coreMat);
    group.add(core);

    // Floating geometric forms
    const floatMat = track(new THREE.LineBasicMaterial({
      color: 0xe2cda0,
      transparent: true,
      opacity: 0.55
    }));
    const floaters = [{
      geo: new THREE.OctahedronGeometry(0.45),
      pos: new THREE.Vector3(4.3, 2.1, -1)
    }, {
      geo: new THREE.IcosahedronGeometry(0.38),
      pos: new THREE.Vector3(-4.1, -1.9, 0.6)
    }, {
      geo: new THREE.TetrahedronGeometry(0.34),
      pos: new THREE.Vector3(-3.3, 2.6, -1.4)
    }, {
      geo: new THREE.BoxGeometry(0.4, 0.4, 0.4),
      pos: new THREE.Vector3(3.6, -2.5, 0.4)
    }].map(({
      geo,
      pos
    }) => {
      track(geo);
      const mesh = new THREE.LineSegments(track(new THREE.EdgesGeometry(geo)), floatMat);
      mesh.position.copy(pos);
      scene.add(mesh);
      return {
        mesh,
        base: pos.y
      };
    });
    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;
    const onPointer = (e: PointerEvent) => {
      targetX = e.clientX / window.innerWidth - 0.5;
      targetY = e.clientY / window.innerHeight - 0.5;
    };
    if (!reduce) window.addEventListener('pointermove', onPointer, {
      passive: true
    });
    let visible = true;
    const io = new IntersectionObserver(([entry]) => visible = entry.isIntersecting);
    io.observe(mount);
    const ro = new ResizeObserver(() => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      if (!width || !height) return;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      if (reduce) renderer.render(scene, camera);
    });
    ro.observe(mount);
    const clock = new THREE.Clock();
    let raf = 0;
    const frame = () => {
      raf = requestAnimationFrame(frame);
      if (!visible || document.hidden) return;
      const t = clock.getElapsedTime();
      curX += (targetX - curX) * 0.04;
      curY += (targetY - curY) * 0.04;
      group.rotation.y = t * 0.05 + curX * 0.5;
      group.rotation.x = 0.25 + curY * 0.3;
      core.rotation.y = -t * 0.3;
      core.rotation.x = t * 0.15;
      floaters.forEach((f, i) => {
        f.mesh.position.y = f.base + Math.sin(t * 0.7 + i * 1.7) * 0.18;
        f.mesh.rotation.x = t * 0.2 + i;
        f.mesh.rotation.y = t * 0.25 + i;
      });
      renderer.render(scene, camera);
    };
    if (reduce) {
      group.rotation.x = 0.25;
      renderer.render(scene, camera);
    } else {
      frame();
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onPointer);
      io.disconnect();
      ro.disconnect();
      disposables.forEach(d => d.dispose());
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [reduce]);
  if (failed) {
    return <div className={`relative ${className}`} aria-hidden="true" data-id="element-591" data-mp="2bx79uc1xw255">
        <GeometricField data-id="element-592" data-mp="2buuxceeyajzh" />
      </div>;
  }
  return <div ref={mountRef} className={className} aria-hidden="true" data-id="element-593" data-mp="2polwqtyywnhh" />;
}